import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { EntityService } from '../../../core/entity/entity.service';
import { ENTITY_TYPE_LABELS, DeletedEntity } from '../../../core/entity/entity.types';
import { IconComponent } from '../../../shared/icon/icon';

@Component({
  selector: 'app-deleted-businesses',
  imports: [MatCardModule, MatButtonModule, RouterLink, IconComponent],
  templateUrl: './deleted-businesses.html',
  styleUrl: './deleted-businesses.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeletedBusinessesComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entities = signal<DeletedEntity[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  /** short_ids currently being restored — disables their button. */
  protected readonly restoringIds = signal<ReadonlySet<string>>(new Set());

  protected getTypeLabel(type: string): string {
    return ENTITY_TYPE_LABELS[type] ?? type;
  }

  protected isRestoring(shortId: string): boolean {
    return this.restoringIds().has(shortId);
  }

  ngOnInit(): void {
    this.loadDeleted();
  }

  protected retryLoad(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.cdr.markForCheck();
    this.loadDeleted();
  }

  private loadDeleted(): void {
    this.entityService
      .getDeletedEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.entities.set(res.entities);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected restore(entity: DeletedEntity): void {
    this.restoringIds.update((ids) => new Set(ids).add(entity.short_id));
    this.cdr.markForCheck();

    this.entityService
      .restoreEntity(entity.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.entities.update((list) => list.filter((e) => e.short_id !== entity.short_id));
          this.clearRestoring(entity.short_id);
          this.snackBar.open(`${entity.name} was restored.`, 'Dismiss', {
            duration: 5000,
          });
          this.cdr.markForCheck();
        },
        error: (err: { error?: { code?: string; detail?: string } }) => {
          this.clearRestoring(entity.short_id);
          const message =
            err.error?.code === 'REG_NO_TAKEN'
              ? (err.error.detail ??
                'This business cannot be restored — its registration number is now in use.')
              : 'Could not restore this business. Please try again.';
          this.snackBar.open(message, 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  private clearRestoring(shortId: string): void {
    this.restoringIds.update((ids) => {
      const next = new Set(ids);
      next.delete(shortId);
      return next;
    });
  }
}
